import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { Client, ClientStatus } from '@core/generated/gql-types';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';

interface ClientSelectorProps {
  value: string;
  onChange: (value: string) => void;
  clients: Client[];
  error?: string;
  touched?: boolean;
}

const ClientSelector: React.FC<ClientSelectorProps> = ({
  value,
  onChange,
  clients,
  error,
  touched,
}) => {
  const { getTerm } = useTemplateStore();
  return (
    <div>
      <label className="mb-1 block text-sm font-medium">
        Select {getTerm(TemplateTerm.CLIENT, false)}
      </label>
      <Listbox value={value} onChange={onChange}>
        <div className="relative">
          <Listbox.Button className="relative w-full cursor-default rounded-lg border bg-white py-2 pr-10 pl-3 text-left shadow-xs focus:outline-hidden focus-visible:border-blue-500 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-blue-300">
            <span className="block truncate">
              {clients.find((c) => c.id === value)?.name ||
                `Select ${getTerm(TemplateTerm.CLIENT, false)}`}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </span>
          </Listbox.Button>
          <Transition
            as={React.Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm">
              {[...(clients || [])]
                .sort((a, b) => (a?.name || '').localeCompare(b?.name || ''))
                .map((client) => {
                  const isPending = client.status === ClientStatus.Pending;
                  return (
                    <Listbox.Option
                      key={client.id}
                      className={({ active, disabled }) =>
                        `relative cursor-default py-2 pr-4 pl-10 select-none ${
                          disabled
                            ? 'cursor-not-allowed bg-gray-100 opacity-50'
                            : active
                              ? 'bg-blue-100 text-blue-900'
                              : 'text-gray-900'
                        }`
                      }
                      value={client.id}
                      disabled={isPending}
                    >
                      {({ selected }) => (
                        <>
                          <span
                            className={`block truncate ${
                              selected ? 'font-medium' : 'font-normal'
                            }`}
                          >
                            {isPending
                              ? `${client.name} - Pending approval`
                              : client.name}
                          </span>
                          {selected ? (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                              <FontAwesomeIcon icon={faCheck} />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Listbox.Option>
                  );
                })}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
      {touched && error && (
        <div className="mt-1 text-xs text-red-500">{error}</div>
      )}
    </div>
  );
};

export default ClientSelector;
